// src/Services/tokenService.js

/**
 * Saves the authentication token to localStorage.
 * @param {string} token The token to be saved.
 */
export const setToken = (token) => {
    if (token) {
        localStorage.setItem("token", token);
    }
};

/**
 * Retrieves the authentication token from localStorage.
 * @returns {string|null} The token, or null if it doesn't exist.
 */
export const getToken = () => {
    return localStorage.getItem("token");
};

/* ============================================================================
   CURRENT CASE  —  the case the user is working in this session.
   ============================================================================
   Set whenever a case is opened: its file list (FilesTable) or its dashboard
   (BoldBIDashboard). The sidebar's Reports link carries no case, so Reports
   opens the current case; with none set it shows the case picker.

   One key per browser, not per user: login clears it (clearReportsLastCase),
   so a second person signing in on the same browser never inherits it. The
   dashboard also checks the case is still in the user's case list before
   opening it. Older builds stored per-user keys under LEGACY_PREFIX; the
   login clear removes those too.
   ========================================================================== */
const CURRENT_CASE_KEY = "efraud.currentCase";
const LEGACY_PREFIX = "efraud.reports.lastCase.";

const isRealCaseId = (v) =>
    v != null && v !== "" && v !== "null" && v !== "undefined";

export const setCurrentCase = (caseId) => {
    if (!isRealCaseId(caseId)) return;
    try { localStorage.setItem(CURRENT_CASE_KEY, String(caseId)); } catch (e) { /* storage unavailable */ }
};

export const getCurrentCase = () => {
    try { return localStorage.getItem(CURRENT_CASE_KEY); } catch (e) { return null; }
};

export const forgetCurrentCase = () => {
    try { localStorage.removeItem(CURRENT_CASE_KEY); } catch (e) { /* storage unavailable */ }
};

/**
 * Forgets the current case, so a fresh login opens Reports on the case picker.
 * Called on successful login rather than on logout because a session can end
 * many ways (sign out, idle timeout, expiry, a 401 on any page) and login is
 * the one place every new session passes through. NOT called from setToken:
 * PrivateRoute also uses setToken to refresh the token mid-session, which must
 * not forget the case.
 */
export const clearReportsLastCase = () => {
    forgetCurrentCase();
    try {
        Object.keys(localStorage)
            .filter((k) => k.startsWith(LEGACY_PREFIX))
            .forEach((k) => localStorage.removeItem(k));
    } catch (e) {
        /* storage unavailable */
    }
};

/**
 * Removes the authentication token from localStorage.
 */
export const removeToken = () => {
    localStorage.removeItem("token");
};

/**
 * Reads the "exp" claim out of a JWT WITHOUT verifying its signature.
 *
 * Verification is the server's job. This only answers "does the browser already
 * know this token is dead?", which lets us
 *   - tell a genuine session expiry apart from a 401 raised for another reason
 *     (see commonRequest.js), and
 *   - route to /login on load instead of rendering an app that will 401 on the
 *     first click (see PrivateRoute).
 *
 * Anything unparseable counts as expired: failing toward logout is the safe
 * direction. A token with no exp claim is left to the server to judge.
 *
 * @param {string|null} token
 * @returns {boolean}
 */
export const isTokenExpired = (token) => {
    if (!token) return true;
    try {
        const payload = token.split(".")[1];
        const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
        const { exp } = JSON.parse(json);
        if (!exp) return false;
        return Date.now() >= exp * 1000;
    } catch (e) {
        return true;
    }
};

/**
 * Convenience inverse of isTokenExpired for the stored token.
 * @returns {boolean} true when a token is present and not past its exp.
 */
export const hasValidToken = () => !isTokenExpired(getToken());
